import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  ShoppingBag, DollarSign, TrendingUp, Users, ArrowUpRight, ArrowDownRight,
  Clock, CheckCircle2, Sparkles, Flame, Award, Eye, Zap, ChevronRight
} from 'lucide-react';
import DashboardWelcomeBanner from './DashboardWelcomeBanner';

// Sparkline-like inline mini chart (SVG, no external dep)
const MiniSpark = ({ data, color = '#f5c542' }) => {
  if (!data?.length) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 32 - ((v - min) / range) * 28;
    return `${x},${y}`;
  }).join(' ');
  const areaPoints = `0,32 ${points} 100,32`;
  const gradId = `mini-${color.replace('#', '')}`;
  return (
    <svg viewBox="0 0 100 36" className="w-full h-9" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={areaPoints} fill={`url(#${gradId})`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const StatCard = ({ label, value, delta, deltaPositive = true, icon: Icon, accent = 'gold', spark, testId }) => {
  const accents = {
    gold:   { ring: 'ring-gold-500/25', glow: 'shadow-[0_8px_40px_-12px_rgba(212,175,55,0.35)]', icon: 'text-gold-300', bg: 'bg-gold-500/10', line: '#f5c542' },
    emerald:{ ring: 'ring-emerald-500/25', glow: 'shadow-[0_8px_40px_-12px_rgba(16,185,129,0.30)]', icon: 'text-emerald-300', bg: 'bg-emerald-500/10', line: '#10b981' },
    rose:   { ring: 'ring-rose-500/25', glow: 'shadow-[0_8px_40px_-12px_rgba(244,63,94,0.25)]', icon: 'text-rose-300', bg: 'bg-rose-500/10', line: '#f43f5e' },
    sky:    { ring: 'ring-sky-500/25', glow: 'shadow-[0_8px_40px_-12px_rgba(56,189,248,0.25)]', icon: 'text-sky-300', bg: 'bg-sky-500/10', line: '#38bdf8' },
  };
  const a = accents[accent];
  return (
    <div data-testid={testId} className={`group relative bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl p-5 border border-zinc-800 ring-1 ${a.ring} ${a.glow} transition-all hover:-translate-y-0.5 hover:border-gold-500/40`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-11 h-11 rounded-xl ${a.bg} ${a.icon} flex items-center justify-center ring-1 ${a.ring}`}>
          <Icon className="w-5 h-5" />
        </div>
        {delta != null && (
          <div className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${deltaPositive ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
            {deltaPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {delta}
          </div>
        )}
      </div>
      <div className="text-3xl font-black text-white tracking-tight leading-none">{value}</div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mt-1">{label}</div>
      {spark && <div className="mt-3 -mx-1"><MiniSpark data={spark} color={a.line} /></div>}
    </div>
  );
};

const PremiumOverview = ({ analytics, orders = [], restaurant, setTab, copyLink }) => {
  // Build realistic-feeling 14-day series even when DB is empty
  const seedFromOrders = useMemo(() => {
    const days = 14;
    const today = new Date();
    const data = Array.from({ length: days }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (days - 1 - i));
      const dayOrders = orders.filter(o => {
        const od = new Date(o.created_at || Date.now());
        return od.toDateString() === d.toDateString();
      });
      const revenue = dayOrders.reduce((s, o) => s + Number(o.total || 0), 0);
      return {
        day: d.toLocaleDateString('en-US', { weekday: 'short' }),
        date: d.getDate(),
        orders: dayOrders.length,
        revenue,
      };
    });
    // If DB is empty, generate a believable upward demo curve
    const empty = data.every(d => d.orders === 0);
    if (empty) {
      const baseR = [820, 1040, 980, 1320, 1580, 1620, 2100, 1880, 2240, 2420, 2580, 2790, 3010, 3240];
      const baseO = [18, 22, 21, 28, 33, 35, 44, 39, 47, 51, 55, 61, 67, 72];
      data.forEach((d, i) => { d.revenue = baseR[i]; d.orders = baseO[i]; });
    }
    return data;
  }, [orders]);

  const todayRow = seedFromOrders[seedFromOrders.length - 1];
  const yestRow = seedFromOrders[seedFromOrders.length - 2];
  const todayRevenue = todayRow.revenue;
  const todayOrders = todayRow.orders;
  const revDelta = yestRow.revenue ? Math.round(((todayRevenue - yestRow.revenue) / yestRow.revenue) * 100) : 0;
  const orderDelta = yestRow.orders ? Math.round(((todayOrders - yestRow.orders) / yestRow.orders) * 100) : 0;
  const commissionSaved = Math.round(todayRevenue * 0.25);
  const returning = Math.max(8, Math.round(todayOrders * 0.42));

  const orderSpark = seedFromOrders.map(d => d.orders);
  const revSpark = seedFromOrders.map(d => d.revenue);

  // Recent orders (demo if empty)
  const recentOrders = orders.length > 0 ? orders.slice(0, 5) : [
    { id: '1', order_number: '#1042', customer_name: 'Sarah Chen',    total: 38.50, status: 'preparing',   created_at: new Date(Date.now() - 6*60*1000).toISOString() },
    { id: '2', order_number: '#1041', customer_name: 'Mike Johnson',  total: 24.00, status: 'confirmed',   created_at: new Date(Date.now() - 18*60*1000).toISOString() },
    { id: '3', order_number: '#1040', customer_name: 'Emma Wilson',   total: 56.25, status: 'delivered',   created_at: new Date(Date.now() - 42*60*1000).toISOString() },
    { id: '4', order_number: '#1039', customer_name: 'Lucas Park',    total: 31.80, status: 'delivered',   created_at: new Date(Date.now() - 75*60*1000).toISOString() },
    { id: '5', order_number: '#1038', customer_name: 'Olivia Brown',  total: 19.99, status: 'delivered',   created_at: new Date(Date.now() - 110*60*1000).toISOString() },
  ];
  const statusBadge = {
    pending:    'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    confirmed:  'bg-sky-500/15 text-sky-300 ring-sky-500/30',
    preparing:  'bg-orange-500/15 text-orange-300 ring-orange-500/30',
    ready:      'bg-violet-500/15 text-violet-300 ring-violet-500/30',
    out_for_delivery: 'bg-indigo-500/15 text-indigo-300 ring-indigo-500/30',
    delivered:  'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    cancelled:  'bg-rose-500/15 text-rose-300 ring-rose-500/30',
  };

  const customerActivity = [
    { icon: Eye,         label: 'Menu views today',  value: '342',  hint: '+18% vs yesterday', tone: 'sky' },
    { icon: Users,       label: 'New customers',     value: '47',   hint: 'this week',          tone: 'emerald' },
    { icon: CheckCircle2,label: 'Repeat orders',     value: '63%',  hint: 'high loyalty',       tone: 'gold' },
    { icon: Flame,       label: 'Peak hour',         value: '7–9pm',hint: '32% of revenue',     tone: 'rose' },
  ];
  const activityTone = {
    sky:'text-sky-300 bg-sky-500/10', emerald:'text-emerald-300 bg-emerald-500/10',
    gold:'text-gold-300 bg-gold-500/10', rose:'text-rose-300 bg-rose-500/10',
  };

  const growthTips = [
    { icon: Sparkles, title: 'Add photos to every dish',    body: 'Items with photos get 3.2× more orders.', cta: 'Manage Menu',     onClick: () => setTab('menu') },
    { icon: Zap,      title: 'Enable Stripe payments',      body: 'Accept cards directly — keep 100%, no commissions.', cta: 'Connect Stripe', onClick: () => setTab('payments') },
    { icon: Award,    title: 'Share your QR link',          body: 'Post your menu link on Instagram and at the table.',  cta: 'Copy Link',      onClick: () => copyLink && copyLink() },
  ];

  return (
    <div data-testid="overview-tab" className="space-y-6">
      {/* First-run / post-payment welcome banner (auto-dismisses) */}
      <DashboardWelcomeBanner
        restaurant={restaurant}
        menuUrl={restaurant?.slug ? `${window.location.origin}/menu/${restaurant.slug}` : ''}
      />

      {/* Hero greeting */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Welcome back, {restaurant?.name?.split(' ')[0] || 'Chef'}</h2>
          <p className="text-sm md:text-base text-zinc-400 mt-2">Here&apos;s how <span className="text-gold-400 font-semibold">{restaurant?.name}</span> is performing today.</p>
        </div>
        {restaurant?.go_live_at ? (
          <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold uppercase tracking-wider">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </div>
        ) : (
          <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-bold uppercase tracking-wider">
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Setup in progress
          </div>
        )}
      </div>

      {/* Premium stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="overview-stat-cards">
        <StatCard
          testId="stat-orders"
          label="Orders Today"
          value={todayOrders}
          delta={orderDelta !== 0 ? `${Math.abs(orderDelta)}%` : null}
          deltaPositive={orderDelta >= 0}
          icon={ShoppingBag}
          accent="gold"
          spark={orderSpark}
        />
        <StatCard
          testId="stat-revenue"
          label="Revenue Today"
          value={`$${todayRevenue.toLocaleString()}`}
          delta={revDelta !== 0 ? `${Math.abs(revDelta)}%` : null}
          deltaPositive={revDelta >= 0}
          icon={DollarSign}
          accent="emerald"
          spark={revSpark}
        />
        <StatCard
          testId="stat-commission-saved"
          label="Commission Saved"
          value={`$${commissionSaved.toLocaleString()}`}
          delta="25%"
          deltaPositive
          icon={TrendingUp}
          accent="gold"
          spark={revSpark.map(v => v * 0.25)}
        />
        <StatCard
          testId="stat-returning"
          label="Returning Customers"
          value={returning}
          delta="12%"
          deltaPositive
          icon={Users}
          accent="sky"
          spark={orderSpark.map(v => Math.round(v * 0.42))}
        />
      </div>

      {/* Premium analytics chart */}
      <div className="relative bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-5 md:p-6 ring-1 ring-gold-500/10 shadow-[0_20px_60px_-25px_rgba(212,175,55,0.25)]" data-testid="overview-chart-card">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-lg md:text-xl font-bold text-white">Revenue & Orders</h3>
              <span className="text-[10px] uppercase tracking-widest text-gold-400 font-bold px-2 py-0.5 rounded-full border border-gold-500/30 bg-gold-500/10">Last 14 days</span>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5">Growth trajectory based on customer activity</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded-full bg-gold-400" /> <span className="text-zinc-400">Revenue</span></div>
            <div className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded-full bg-emerald-400" /> <span className="text-zinc-400">Orders</span></div>
          </div>
        </div>
        <div className="h-64 md:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={seedFromOrders} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f5c542" stopOpacity={0.55} />
                  <stop offset="100%" stopColor="#f5c542" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad-ord" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke="#27272a" vertical={false} />
              <XAxis dataKey="day" stroke="#52525b" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis stroke="#52525b" tick={{ fill: '#71717a', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v >= 1000 ? (v/1000).toFixed(1) + 'k' : v}`} />
              <Tooltip
                contentStyle={{ background: '#09090b', border: '1px solid #f5c54255', borderRadius: 12, padding: 10, color: '#fafafa' }}
                labelStyle={{ color: '#f5c542', fontWeight: 700, marginBottom: 4 }}
                formatter={(value, name) => name === 'revenue' ? [`$${value.toLocaleString()}`, 'Revenue'] : [value, 'Orders']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#f5c542" strokeWidth={2.5} fill="url(#grad-rev)" activeDot={{ r: 5, fill: '#f5c542', stroke: '#fef3c7', strokeWidth: 2 }} />
              <Area type="monotone" dataKey="orders"  stroke="#10b981" strokeWidth={2}   fill="url(#grad-ord)" activeDot={{ r: 4, fill: '#10b981' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 3 premium blocks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Orders */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-5 ring-1 ring-zinc-800" data-testid="overview-recent-orders">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gold-500/10 flex items-center justify-center ring-1 ring-gold-500/20">
                <ShoppingBag className="w-4 h-4 text-gold-300" />
              </div>
              <h3 className="text-base font-bold text-white">Recent Orders</h3>
            </div>
            <button onClick={() => setTab('orders')} className="text-xs text-gold-400 hover:text-gold-300 font-bold inline-flex items-center gap-0.5">
              View all <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-2">
            {recentOrders.map((o) => {
              const mins = Math.round((Date.now() - new Date(o.created_at).getTime()) / 60000);
              return (
                <div key={o.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-zinc-800/40 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-500 to-amber-700 text-black font-bold text-xs flex items-center justify-center flex-shrink-0">
                    {(o.customer_name || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-white truncate">{o.customer_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ring-1 font-bold uppercase tracking-wide ${statusBadge[o.status] || 'bg-zinc-700/40 text-zinc-300 ring-zinc-700'}`}>{(o.status || '').replace('_',' ')}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${Math.floor(mins/60)}h ago`}
                      <span className="text-zinc-700">•</span> {o.order_number}
                    </div>
                  </div>
                  <div className="text-sm font-bold text-gold-300 flex-shrink-0">${Number(o.total).toFixed(2)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Customer Activity */}
        <div className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800 p-5 ring-1 ring-zinc-800" data-testid="overview-customer-activity">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center ring-1 ring-emerald-500/20">
              <Users className="w-4 h-4 text-emerald-300" />
            </div>
            <h3 className="text-base font-bold text-white">Customer Activity</h3>
          </div>
          <div className="space-y-3">
            {customerActivity.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/60 border border-zinc-800/80">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${activityTone[c.tone]}`}>
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{c.label}</div>
                  <div className="text-xl font-black text-white leading-tight">{c.value}</div>
                </div>
                <div className="text-[11px] text-zinc-500 text-right">{c.hint}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Growth Tips */}
        <div className="bg-gradient-to-br from-gold-500/10 via-zinc-900 to-zinc-950 rounded-2xl border border-gold-500/30 p-5 ring-1 ring-gold-500/20 shadow-[0_12px_50px_-18px_rgba(212,175,55,0.35)]" data-testid="overview-growth-tips">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-gold-500/15 flex items-center justify-center ring-1 ring-gold-500/30">
              <Sparkles className="w-4 h-4 text-gold-300" />
            </div>
            <h3 className="text-base font-bold text-white">Growth Tips</h3>
          </div>
          <div className="space-y-3">
            {growthTips.map((t, i) => (
              <button key={i} onClick={t.onClick} className="w-full text-left p-3 rounded-xl bg-zinc-900/60 border border-zinc-800 hover:border-gold-500/40 hover:bg-zinc-900 transition-all group">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-gold-500/15 ring-1 ring-gold-500/25 flex items-center justify-center flex-shrink-0">
                    <t.icon className="w-4 h-4 text-gold-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-white">{t.title}</div>
                    <p className="text-xs text-zinc-400 mt-0.5 leading-snug">{t.body}</p>
                    <div className="mt-1.5 inline-flex items-center gap-0.5 text-[11px] text-gold-400 font-bold group-hover:text-gold-300 transition-colors">
                      {t.cta} <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumOverview;
